/**
 * The reader's stepping, out of the `.tsx` so it runs under `node --test`.
 * Nothing turns a page by itself (storybooks PRD §5.2): every step here is a
 * tap or a swipe.
 */

import type { Register } from '../profile/register';

// The title page (amendment 2026-09-29): the cover, the title, and a Read
// button. It is silent and gives the browser the user gesture page 1's
// autoplay needs (R6).
export type ReaderStep = { kind: 'cover' } | { kind: 'page'; index: number } | { kind: 'finished' };

export const start = (): ReaderStep => ({ kind: 'cover' });
export const again = start;

export function next(step: ReaderStep, count: number): ReaderStep {
  if (step.kind === 'finished') return step;
  if (step.kind === 'cover') return { kind: 'page', index: 0 };
  return step.index + 1 >= count ? { kind: 'finished' } : { kind: 'page', index: step.index + 1 };
}

export function back(step: ReaderStep): ReaderStep {
  if (step.kind === 'finished') return step;
  if (step.kind === 'cover') return step;
  return step.index === 0 ? { kind: 'cover' } : { kind: 'page', index: step.index - 1 };
}

/** The next page's id, so turning to it does not wait on synthesis (R6). */
export function prefetchFor(pages: ReadonlyArray<{ id: string }>, index: number): string | null {
  return pages[index + 1]?.id ?? null;
}

/**
 * Far enough that a tap on a button, which drifts a few pixels, never turns
 * the page.
 */
const SWIPE_PX = 60;

/** Horizontal travel of one pointer, left meaning forward. */
export function swipe(dx: number): 'next' | 'back' | null {
  if (dx <= -SWIPE_PX) return 'next';
  if (dx >= SWIPE_PX) return 'back';
  return null;
}

/**
 * Kid: each page reads itself after the title page's tap (§5.2). Adult:
 * never, because an adult reads a long page faster than TTS says it
 * (amendment 2026-09-29). Whether the page is fetched at all -- so that once
 * an adult has asked once, Hear it is instant from then on -- is
 * `fetchesAhead`'s call, not this one (PRD amendment "what phase 1b shipped", R7).
 */
export const autoplays = (register: Register): boolean => register === 'kid';

/** The end of a book: a child's star and celebration, or an adult's quiet "Finished". */
export const celebrates = (register: Register): boolean => register === 'kid';

/** A button on the finished step. */
export type FinishedAction = 'again' | 'talk' | 'shelf';

/**
 * The finished step's buttons, in order (storybooks phase 2 §1): Talk about
 * it sits beside Read it again for a child, and first, under "Finished", for
 * an adult.
 */
export const finishedActions = (register: Register): readonly FinishedAction[] =>
  register === 'kid' ? ['again', 'talk', 'shelf'] : ['talk', 'again', 'shelf'];

/** What marks a finished book on the shelf: a sticker for a child, a check for an adult. */
export const finishedMark = (register: Register): 'star' | 'check' => (register === 'kid' ? 'star' : 'check');

/**
 * A page's line and its gloss. The kid's values are the phase-1 reader's,
 * unchanged. An adult's page is one paragraph in body type.
 */
export function pageLook(register: Register): { text: string; gloss: string } {
  return register === 'kid'
    ? { text: 'text-2xl font-semibold', gloss: 'text-base-content/60 text-base' }
    : { text: 'w-full text-start text-base leading-relaxed', gloss: 'text-base-content/60 w-full text-start text-sm' };
}

/**
 * Whether the reader fetches ahead of a tap. A kid's book autoplays, so
 * it always does. An adult's does once they have asked to hear a page, so
 * a book read silently costs no synthesis (amendment 2026-09-29, R7).
 */
export const fetchesAhead = (register: Register, heard: boolean): boolean => register === 'kid' || heard;
